import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  PenTool,
  Clock,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  Menu,
  X,
  Sparkles,
  BookMarked,
  Layout,
  Mail,
  Wand2,
  Phone,
  PhoneCall
} from 'lucide-react';

// NavItem component defined outside
function NavItem({ item, sidebarCollapsed, onItemClick }) {
  const badge = item.badge;
  const hasBadge = typeof badge === 'number' && badge > 0;
  const badgeLabel = badge > 99 ? '99+' : badge;

  if (item.disabled) {
    return (
      <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/30 cursor-not-allowed ${sidebarCollapsed ? 'justify-center' : ''}`}>
        <item.icon className="w-5 h-5 flex-shrink-0" />
        {!sidebarCollapsed && (
          <>
            <span className="text-sm">{item.name}</span>
            <span className="ml-auto text-xs bg-white/10 px-2 py-0.5 rounded">Soon</span>
          </>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      onClick={onItemClick}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${sidebarCollapsed ? 'justify-center' : ''} ${
          isActive
            ? 'bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/30'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {!sidebarCollapsed && <span className="text-sm">{item.name}</span>}

      {hasBadge && (
        sidebarCollapsed ? (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold bg-[#00d4aa] text-black rounded-full">
            {badgeLabel}
          </span>
        ) : (
          <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-bold bg-[#00d4aa] text-black rounded-full">
            {badgeLabel}
          </span>
        )
      )}
    </NavLink>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PENDING_COUNT_MIN_INTERVAL_MS = 30000; // don't refetch more than every 30s

function WorkHubLayout({ children }) {
  const { user, logout, isClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const lastFetchRef = useRef(0);

  // Fetch the approval-queue count on mount and on route change (throttled).
  // Client-role users don't have a queue, so skip the call for them.
  useEffect(() => {
    if (isClient) return;

    const now = Date.now();
    if (now - lastFetchRef.current < PENDING_COUNT_MIN_INTERVAL_MS) return;
    lastFetchRef.current = now;

    let cancelled = false;
    const fetchPendingCount = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/summary`);
        if (!cancelled) {
          setPendingCount(response.data?.pending_approvals?.count || 0);
        }
      } catch (error) {
        // Badge is best-effort — silently ignore failures.
        console.error('Failed to load pending count:', error);
      }
    };

    fetchPendingCount();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, isClient]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Different nav for client users
  const clientNavItems = [
    {
      section: 'OVERVIEW',
      items: [
        { name: 'My Dashboard', path: '/client-dashboard', icon: LayoutDashboard }
      ]
    }
  ];

  const adminNavItems = [
    {
      section: 'OVERVIEW',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard }
      ]
    },
    {
      section: 'CAMPAIGNS',
      items: [
        { name: 'Approval Queue', path: '/campaigns/queue', icon: CheckSquare, badge: pendingCount },
        { name: 'Create Campaign', path: '/campaigns/create', icon: PenTool },
        { name: 'Email Creation', path: '/email-campaigns', icon: Mail },
        { name: 'Drafts', path: '/campaigns/drafts', icon: Clock },
        { name: 'All Campaigns', path: '/campaigns', icon: FolderOpen }
      ]
    },
    {
      section: 'OFFERS',
      items: [
        { name: 'Build New Offer', path: '/offers/build', icon: Sparkles },
        { name: 'Saved Offers', path: '/offers/saved', icon: BookMarked },
        { name: 'Offer Templates', path: '/offers/templates', icon: Layout }
      ]
    },
    {
      section: 'SALES',
      items: [
        { name: 'Triage Calls', path: '/triage', icon: PhoneCall },
        { name: 'New Triage', path: '/triage/new', icon: Phone }
      ]
    },
    {
      section: 'CONTENT',
      items: [
        { name: 'Creative Generator', path: '/creative-generator', icon: Wand2 },
        { name: 'Repurpose Content', path: '/create', icon: FileText },
        { name: 'Content Library', path: '/content/library', icon: FolderOpen, disabled: true }
      ]
    },
    {
      section: 'SETTINGS',
      items: [
        ...(user?.role === 'admin' ? [
          { name: 'Admin Panel', path: '/admin', icon: Shield }
        ] : [])
      ]
    }
  ];

  const navItems = isClient ? clientNavItems : adminNavItems;

  // Focus / "call mode": on the ACTIVE triage-call cockpit route only
  // (/triage/:callId), hide the full app sidebar so a stray click can't
  // navigate away while a prospect is on the line. The cockpit renders its
  // own "Back to calls" exit control. Explicitly EXCLUDE the list (/triage),
  // the new-call picker (/triage/new) and the summary (/triage/:callId/summary)
  // so every other route's layout is untouched.
  const isCallMode = /^\/triage\/(?!new(?:\/|$))[^/]+\/?$/.test(location.pathname);

  // Render sidebar content
  const renderSidebarContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-white/10 ${sidebarCollapsed ? 'text-center' : ''}`}>
        {sidebarCollapsed ? (
          <div className="w-10 h-10 mx-auto bg-gradient-to-br from-[#00d4aa] to-[#00b894] rounded-lg flex items-center justify-center">
            <span className="text-black font-bold text-lg">E</span>
          </div>
        ) : (
          <div>
            <h1 className="text-lg font-bold text-white">EPSILLON MEDIA</h1>
            <p className="text-xs text-[#00d4aa] font-mono tracking-wider">WORK HUB</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
        {navItems.map((section, idx) => (
          <div key={idx}>
            {!sidebarCollapsed && section.items.length > 0 && (
              <p className="text-xs font-mono text-white/40 uppercase tracking-wider mb-2 px-3">
                {section.section}
              </p>
            )}
            <div className="space-y-1">
              {section.items.map((item, itemIdx) => (
                <NavItem 
                  key={itemIdx} 
                  item={item} 
                  sidebarCollapsed={sidebarCollapsed}
                  onItemClick={closeMobileMenu}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-white/10">
        {!sidebarCollapsed && (
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00d4aa] to-[#00b894] flex items-center justify-center text-black font-bold text-sm">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/40 capitalize">{user?.role}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full text-white/60 hover:text-white hover:bg-white/5 ${sidebarCollapsed ? 'justify-center' : 'justify-start'}`}
        >
          <LogOut className="w-4 h-4" />
          {!sidebarCollapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>

      {/* Collapse Toggle - Desktop Only */}
      <div className="hidden md:block p-3 border-t border-white/10">
        <Button
          variant="ghost"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="w-full text-white/40 hover:text-white hover:bg-white/5"
        >
          {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>
    </>
  );

  // Call mode: no sidebar, no mobile chrome — full-width cockpit. The cockpit
  // header supplies the deliberate "Back to calls" exit so the user is never
  // stranded without nav.
  if (isCallMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a]">
        <main className="min-h-screen">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a]">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 glass-morphism border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-white">EPSILLON MEDIA</h1>
          <p className="text-xs text-[#00d4aa] font-mono">WORK HUB</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-white"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`md:hidden fixed top-0 left-0 h-full w-64 bg-[#0d0d0d] border-r border-white/10 z-50 transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full pt-16">
          {renderSidebarContent()}
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex fixed left-0 top-0 h-full bg-[#0d0d0d]/80 backdrop-blur-xl border-r border-white/10 flex-col transition-all duration-300 z-40 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
        {renderSidebarContent()}
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'} pt-14 md:pt-0`}>
        <main className="min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}

export default WorkHubLayout;
